import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { SingleDayEvent } from '../utils/careHomeCardFormatting';

interface EventPopupProps {
    events: SingleDayEvent[];
    careHomeName: string;
    onClose: () => void;
}

const EventPopup: React.FC<EventPopupProps> = ({ events, careHomeName, onClose }) => {
    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);

    const overlayStyle: React.CSSProperties = {
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.45)',
        padding: '1rem',
    };

    const panelStyle: React.CSSProperties = {
        position: 'relative',
        width: '100%',
        maxWidth: '420px',
        maxHeight: '80vh',
        borderRadius: '16px',
        background: '#ffffff',
        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
        fontFamily: '"Trebuchet MS", sans-serif',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    };

    const headerStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.8rem 1rem',
        background: 'linear-gradient(135deg, #025150 0%, #0a7f72 100%)',
        color: '#ffffff',
    };

    const closeBtnStyle: React.CSSProperties = {
        background: 'none',
        border: 'none',
        color: '#ffffff',
        fontSize: '1.4rem',
        cursor: 'pointer',
        lineHeight: 1,
        padding: '0 0.2rem',
        opacity: 0.85,
    };

    const listStyle: React.CSSProperties = {
        overflowY: 'auto',
        padding: '0.6rem 0.8rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.7rem',
    };

    const cardStyle: React.CSSProperties = {
        borderRadius: '12px',
        border: '1px solid rgba(2, 81, 80, 0.12)',
        background: '#f4f3f1',
        padding: '0.7rem 0.8rem',
    };

    const nameStyle: React.CSSProperties = {
        margin: 0,
        fontSize: '0.92rem',
        fontWeight: 700,
        color: '#025150',
        lineHeight: 1.2,
    };

    const descStyle: React.CSSProperties = {
        margin: '0.24rem 0 0',
        fontSize: '0.76rem',
        color: '#4f5960',
        lineHeight: 1.3,
    };

    const timeStyle: React.CSSProperties = {
        margin: '0.4rem 0 0',
        fontSize: '0.78rem',
        fontWeight: 600,
        color: '#0d5f57',
    };

    const contactStyle: React.CSSProperties = {
        marginTop: '0.4rem',
        paddingTop: '0.36rem',
        borderTop: '1px solid #e0e2e4',
        fontSize: '0.72rem',
        color: '#4f5960',
        lineHeight: 1.45,
    };

    const linkStyle: React.CSSProperties = {
        color: '#025150',
        textDecoration: 'none',
        fontWeight: 600,
    };

    const formatPhone = (phone: string): string => {
        if (!phone) return '';
        const digits = phone.replace(/\D/g, '');
        if (digits.startsWith('44') && digits.length > 2) return `+${digits.slice(0, 2)} ${digits.slice(2)}`;
        return phone;
    };

    return createPortal(
        <div style={overlayStyle} onClick={onClose}>
            <div style={panelStyle} onClick={e => e.stopPropagation()}>
                <div style={headerStyle}>
                    <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>
                        Events &mdash; {careHomeName}
                    </span>
                    <button style={closeBtnStyle} onClick={onClose} aria-label="Close">&times;</button>
                </div>

                <div style={listStyle}>
                    {events.map((ev, i) => (
                        <div key={i} style={cardStyle}>
                            <p style={nameStyle}>{ev.event_name}</p>
                            {ev.event_description && (
                                <p style={descStyle}>{ev.event_description}</p>
                            )}
                            <p style={timeStyle}>
                                {ev.formattedDate} &middot; {ev.formattedStartTime} &ndash; {ev.formattedEndTime}
                            </p>

                            {(ev.contact_name || ev.contact_phone || ev.contact_email) && (
                                <div style={contactStyle}>
                                    {ev.contact_name && <div>{ev.contact_name}</div>}
                                    {ev.contact_phone && (
                                        <div>
                                            <a href={`tel:+${ev.contact_phone.replace(/\D/g, '')}`} style={linkStyle}>
                                                {formatPhone(ev.contact_phone)}
                                            </a>
                                        </div>
                                    )}
                                    {ev.contact_email && (
                                        <div>
                                            <a href={`mailto:${ev.contact_email}`} style={linkStyle}>
                                                {ev.contact_email}
                                            </a>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default EventPopup;
